import React, { useState, useEffect } from 'react';
import { courseAPI, userAPI, lessonAPI, cacheUtils } from '../../services/api';
import FileUpload from '../ui/FileUpload';
import { parse } from 'uuid';


const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLessonsModalOpen, setIsLessonsModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseLessons, setCourseLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [deletingCourseId, setDeletingCourseId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructor: '',
    price : '',
    duration_weeks: '',
    thumbnail_url: '',
  });
  
  

  useEffect(() => {
    fetchCourses();
  }, []);
  
  useEffect(() => {
    if (isModalOpen) {
      fetchInstructors();
    }
  }, [isModalOpen]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstructors = async () => {
    try {
      setInstructorsLoading(true);
      const response = await userAPI.getInstructors();
      setInstructors(response.data || []);
    } catch (error) {
      console.error('Error fetching instructors:', error);
    } finally {
      setInstructorsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        price: parseInt(formData.price),
        duration_weeks: parseInt(formData.duration_weeks)
      };
      
      setLoading(true);
      console.log(`${isEditing ? 'Updating' : 'Creating'} course data:`, submitData);
      
      if (isEditing) {
        // Add ID for update
        submitData.id = editingCourse.id;
        await courseAPI.updateCourse(submitData);
      } else {
        await courseAPI.createCourse(submitData);
      }
      
      setIsModalOpen(false);
      resetForm();
      // The cache will be automatically invalidated by the API service
      // But we'll fetch fresh data to update the UI immediately
      fetchCourses();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} course:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCourse = (course) => {
    setIsEditing(true);
    setEditingCourse(course);
    setFormData({
      title: course.title || '',
      description: course.description || '',
      instructor: course.instructor || '',
      price: course.price ? course.price.toString() : '',
      duration_weeks: course.duration_weeks ? course.duration_weeks.toString() : '',
      thumbnail_url: course.thumbnail_url || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ 
      title: '', 
      description: '', 
      instructor: '', 
      price: '', 
      duration_weeks: '', 
      thumbnail_url: '' 
    });
    setIsEditing(false);
    setEditingCourse(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingCourseId(courseId);
      console.log('Deleting course with ID:', courseId);
      await courseAPI.deleteCourse(courseId);
      // The cache will be automatically invalidated by the API service
      // Update the UI by fetching fresh data
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    } finally {
      setDeletingCourseId(null);
    }
  };

  const handleSeeLessons = async (course) => {
    try {
      setSelectedCourse(course);
      setLessonsLoading(true);
      setIsLessonsModalOpen(true);
      
      console.log('Fetching lessons for course ID:', course.id);
      const response = await lessonAPI.getAllLessonsByCourseId(course.id);
      setCourseLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      alert('Failed to load lessons. Please try again.');
    } finally {
      setLessonsLoading(false);
    }
  };

  const handleCloseLessonsModal = () => {
    setIsLessonsModalOpen(false);
    setSelectedCourse(null);
    setCourseLessons([]);
  };

  return (
    <div className="max-w-6xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Course Management</h1>
            <p className="text-lg text-gray-600">Create and manage courses</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                cacheUtils.clearCourseCache();
                fetchCourses();
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => cacheUtils.clearAllCache()}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              🗑️ Clear All Cache
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div 
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create New Course</h3>
          <p className="text-gray-600">Click to add a new course</p>
        </div>

        {loading ? (
          // Loading skeleton
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm animate-pulse">
              <div className="w-12 h-12 mb-4 bg-gray-200 rounded-full"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded mb-3"></div>
              <div className="flex items-center justify-between">
                <div className="h-3 bg-gray-200 rounded w-16"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))
        ) : (
          courses.map((course) => (
            <div key={course.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow relative flex flex-col">
              {/* Action buttons */}
              <div className="absolute top-4 right-4 flex space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditCourse(course);
                  }}
                  className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                  title="Edit course"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCourse(course.id);
                  }}
                  disabled={deletingCourseId === course.id}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete course"
                >
                  {deletingCourseId === course.id ? (
                    <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Card content */}
              <div className="flex-1">
                <div className="w-12 h-12 mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 pr-16">{course.title}</h3>
                <p className="text-gray-600 mb-3">{course.description}</p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span><b>{course.price + '₮'}</b></span>
                  <span>{course.duration_weeks + ' weeks'}</span>
                </div>
              </div>
              
              {/* See Lessons Button - Always at bottom */}
              <div className="mt-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSeeLessons(course);
                  }}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>See Lessons</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Course' : 'Create New Course'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructor
                </label>
                <select
                  name="instructor"
                  value={formData.instructor}
                  onChange={handleInputChange}
                  required
                  disabled={instructorsLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                >
                  <option value="">
                    {instructorsLoading ? 'Loading instructors...' : 'Select Instructor'}
                  </option>
                  {!instructorsLoading && instructors.map((inst) => (
                    <option key={inst.id} value={inst.first_name + ' ' + inst.last_name}>
                      {inst.first_name} {inst.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 mt-3">
                  Price
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  name="duration_weeks"
                  value={formData.duration_weeks}
                  onChange={handleInputChange}
                  required
                  placeholder="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thumbnail picture
                </label>
                <FileUpload
                  onUploadSuccess={(aws_url) => {
                    setFormData(prev => ({
                      ...prev,
                      thumbnail_url : aws_url
                    }))
                  }}
                  currentUrl={formData.thumbnail_url}
                  showPreview={true}
                />
                {formData.thumbnail_url && (
                  <div className="mt-2">
                    <p className="text-xs text-green-600">Thumbnail uploaded successfully</p>
                    <a 
                      href={formData.thumbnail_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      View current thumbnail
                    </a>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading 
                    ? (isEditing ? 'Updating...' : 'Creating...') 
                    : (isEditing ? 'Update Course' : 'Create Course')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lessons Modal */}
      {isLessonsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Lessons for "{selectedCourse?.title}"
              </h2>
              <button
                onClick={handleCloseLessonsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh]">
              {lessonsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="ml-2 text-gray-600">Loading lessons...</span>
                </div>
              ) : courseLessons.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No lessons found</h3>
                  <p className="text-gray-600">This course doesn't have any lessons yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {courseLessons.map((lesson, index) => (
                    <div key={lesson.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white text-xs font-medium rounded-full">
                              {lesson.order_index || index + 1}
                            </span>
                            <h3 className="text-lg font-semibold text-gray-900">{lesson.title}</h3>
                          </div>
                          <p className="text-gray-600 mb-3">{lesson.description}</p>
                          {lesson.content && (
                            <div className="mb-3">
                              <p className="text-sm text-gray-500 mb-1">Content Preview:</p>
                              <p className="text-sm text-gray-700 bg-white p-2 rounded border">
                                {lesson.content.length > 150 
                                  ? `${lesson.content.substring(0, 150)}...` 
                                  : lesson.content
                                }
                              </p>
                            </div>
                          )}
                          {lesson.video_url && (
                            <div className="mb-3">
                              <a 
                                href={lesson.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>View Video</span>
                              </a>
                            </div>
                          )}
                          <div className="flex items-center text-sm text-gray-500">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Created: {new Date(lesson.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <div className="text-sm text-gray-600">
                {courseLessons.length} lesson{courseLessons.length !== 1 ? 's' : ''} found
              </div>
              <button
                onClick={handleCloseLessonsModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;